import React from 'react'
import ReactDOM from 'react-dom/client'
import { HashRouter } from 'react-router-dom'
import App from './App.jsx'
import './index.css'

// Log browser and device information for debugging
window.addEventListener('load', () => {
  console.log('App loaded - Debug info:')
  console.log('Full URL:', window.location.href)
  console.log('Path:', window.location.pathname)
  console.log('Search:', window.location.search)
  console.log('Hash:', window.location.hash)
  console.log('User Agent:', navigator.userAgent)
  console.log('Platform:', navigator.platform)

  // Parse URL parameters as a fallback
  try {
    // Parse from direct URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    console.log('URL parameters from window.location.search:')
    for (const [key, value] of urlParams.entries()) {
      console.log(`- ${key}: ${value}`)
    }

    // Parse from hash (for HashRouter)
    const hashParts = window.location.hash.split('?')
    if (hashParts.length > 1) {
      const hashParams = new URLSearchParams(hashParts[1])
      console.log('URL parameters from hash:')
      for (const [key, value] of hashParams.entries()) {
        console.log(`- ${key}: ${value}`)
      }
    }

    // Try a regex approach as last resort
    const url = window.location.href
    const midMatch = url.match(/[?&#](mid|merchant_id|merchantId|merchant-id)=([^&#]*)/i)
    if (midMatch && midMatch[2]) {
      console.log('Merchant ID from regex:', decodeURIComponent(midMatch[2]))
    }
  } catch (error) {
    console.error('Error parsing URL parameters:', error)
  }
  
  // If we have URL parameters outside the hash, move them to hash
  const normalizeUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasUrlParams = urlParams.has('mid') || urlParams.has('loc') || urlParams.has('sid');
    
    // Check if we have hash parameters
    const hashString = window.location.hash;
    const hasHashQuery = hashString && hashString.includes('?');
    
    // If we have URL parameters but no hash query, or both
    if (hasUrlParams) {
      console.log("Found URL parameters, normalizing to hash format");
      
      // Get all parameters from URL
      const params = {};
      for (const [key, value] of urlParams.entries()) {
        params[key] = value;
      }
      
      // If we also have hash parameters, merge them
      if (hasHashQuery) {
        const hashQueryString = hashString.substring(hashString.indexOf('?'));
        const hashParams = new URLSearchParams(hashQueryString);
        
        for (const [key, value] of hashParams.entries()) {
          // URL params take precedence if both exist
          if (!params[key]) {
            params[key] = value;
          }
        }
      }
      
      // Build new hash
      const newParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        newParams.set(key, value);
      });
      
      // Get hash path (everything before the ?)
      let hashPath = '/';
      if (hasHashQuery) {
        hashPath = hashString.substring(1, hashString.indexOf('?')) || '/';
      } else if (hashString) {
        hashPath = hashString.substring(1) || '/';
      }
      
      // Create new URL
      const newUrl = `${window.location.origin}${window.location.pathname}#${hashPath}?${newParams.toString()}`;
      
      // Replace history without reloading
      window.history.replaceState(null, '', newUrl);
      
      console.log("URL normalized:", newUrl);
    }
  };
  
  // Run URL normalization on page load
  normalizeUrl();
})

// Handle hash changes
window.addEventListener('hashchange', () => {
  console.log('Hash changed:', window.location.hash)
  window.dispatchEvent(new CustomEvent('urlParamsChanged'))
})

// Error handler for uncaught errors
window.addEventListener('error', (event) => {
  console.error('Global error caught:', event.error)
})

// Error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason)
})

// Special handling for Safari on iOS
const iOSSafari = /iP(ad|hone|od).+Version\/[\d\.]+.*Safari/i.test(navigator.userAgent)
if (iOSSafari) {
  console.log('iOS Safari detected - applying special handling')
  
  // Add extra error handling specific to iOS Safari
  window.onerror = function(message, source, lineno, colno, error) {
    console.error('iOS Safari error:', { message, source, lineno, colno })
    return false // Let default handler run too
  }
}

// Custom HashRouter that preserves search params
const CustomHashRouter = ({ children }) => {
  return (
    <HashRouter>
      {children}
    </HashRouter>
  );
};

// Render with error boundary
const renderApp = () => {
  try {
    ReactDOM.createRoot(document.getElementById('root')).render(
      <React.StrictMode>
        <ErrorBoundary>
          <CustomHashRouter>
            <App />
          </CustomHashRouter>
        </ErrorBoundary>
      </React.StrictMode>
    )
  } catch (error) {
    console.error('Error rendering app:', error)
    document.getElementById('root').innerHTML = `
      <div style="padding: 20px;text-align: center;font-family: system-ui,sans-serif;">
        <h2 style="color: #b91c1c;">Something went wrong</h2>
        <p>The application encountered an error. Please refresh the page to try again.</p>
        <button onclick="window.location.reload()" style="background: #dc2626;color: white;border: none;padding: 8px 16px;border-radius: 4px;margin-top: 16px;cursor: pointer;">
          Refresh Page
        </button>
      </div>
    `
  }
}

// Simple Error Boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: "20px",
          margin: "20px",
          backgroundColor: "#fef2f2",
          border: "1px solid #fee2e2",
          borderRadius: "8px",
          fontFamily: "system-ui,sans-serif"
        }}>
          <h2 style={{ color: "#b91c1c", marginBottom: "12px" }}>Something went wrong</h2>
          <p style={{ marginBottom: "16px" }}>
            The application encountered an error. You can try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: "#dc2626",
              color: "white",
              border: "none",
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Refresh Page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

renderApp()